import React, { HTMLProps, useContext, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { IconElements } from '../../../config/icons';
import { reverseFormatNumber } from '../../../utils/parsers';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { Account, AccountType, PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { BackButton, NextButton } from './NavigationButtons';
import { RemoveAccountModal } from '../supplement-overlay/RemoveAccountModal';
import {
    AutoFillMoneyAvailableAtDateWithPensionDate,
    SupplementGeneralModal,
} from '../supplement-overlay/SupplementGeneralModal';
import { SavingButtons } from './DialogueDataSaving/PensionProgressSaver';
import { MAX_NUMBER_SUPPLEMENT_ACCS, riskProfiles } from '../../../config/constants';
import { isPensionCalculatedTogether } from '../../../utils/functions';
import { Button } from '../../general/Button';

export type AccountReturnVoid = (account: Account) => void;

export const SupplementSection: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo, personalInfo },
        actions,
    } = useContext(PensionContext);

    const getEmptySavingsAccount = () => {
        return {
            type: 'investment',
            recurringPaymentInterval: 'month',
            riskProfile: riskProfiles.neutral,
        } as Account;
    };

    const withPartner = isPensionCalculatedTogether(personalInfo);
    return (
        <CocktailWrapperScreen
            page={4}
            title={
                isPartnerSection
                    ? t('pension-cocktail-fill-screen-supplement-partner-title')
                    : t('pension-cocktail-fill-screen-supplement-title')
            }
            subTitle={
                isPartnerSection
                    ? t('pension-cocktail-fill-screen-supplement-partner-subtitle')
                    : t('pension-cocktail-fill-screen-supplement-subtitle')
            }
        >
            <YesNoFormGroup
                className="mb-8"
                title={{
                    label: isPartnerSection
                        ? t('pension-cocktail-fill-screen-supplement-partner-btn-label')
                        : t('pension-cocktail-fill-screen-supplement-btn-label'),
                }}
                value={
                    isPartnerSection
                        ? supplementInfo?.partner?.accountsAvailable === undefined
                            ? null
                            : supplementInfo?.partner?.accountsAvailable
                            ? 'yes'
                            : 'no'
                        : supplementInfo?.self?.accountsAvailable === undefined
                        ? null
                        : supplementInfo?.self?.accountsAvailable
                        ? 'yes'
                        : 'no'
                }
                onChange={(value) => {
                    if (value === 'yes') {
                        isPartnerSection
                            ? supplementInfo?.partner?.accounts.length === 0
                                ? actions.setParameters({
                                      supplementInfo: {
                                          partner: { accountsAvailable: true, accounts: [getEmptySavingsAccount()] },
                                      },
                                  })
                                : actions.setParameters({ supplementInfo: { partner: { accountsAvailable: true } } })
                            : supplementInfo?.self?.accounts.length === 0
                            ? actions.setParameters({
                                  supplementInfo: {
                                      self: { accountsAvailable: true, accounts: [getEmptySavingsAccount()] },
                                  },
                              })
                            : actions.setParameters({ supplementInfo: { self: { accountsAvailable: true } } });
                    }
                    if (value === 'no') {
                        isPartnerSection
                            ? actions.setParameters({ supplementInfo: { partner: { accountsAvailable: false } } })
                            : actions.setParameters({ supplementInfo: { self: { accountsAvailable: false } } });
                    }
                }}
            />
            {!!supplementInfo?.self?.accountsAvailable && !isPartnerSection && (
                <AccountForms isPartnerSection={false} />
            )}
            {!!supplementInfo?.partner?.accountsAvailable && isPartnerSection && (
                <AccountForms isPartnerSection={true} />
            )}
            <div className="flex flex-row flex-wrap gap-4 items-start my-8">
                <BackButton />
                <NextButton
                    nextPage={isPartnerSection || !withPartner ? 'results' : 'supplement-partner'}
                    isDisabled={
                        isPartnerSection
                            ? supplementInfo?.partner?.accountsAvailable &&
                              !AreAccountsValid(supplementInfo?.partner?.accounts ?? [])
                            : supplementInfo?.self?.accountsAvailable &&
                              !AreAccountsValid(supplementInfo?.self?.accounts ?? [])
                    }
                />
            </div>
            <SavingButtons />
        </CocktailWrapperScreen>
    );
};

const AccountForms: React.FC<{ isPartnerSection: boolean } & HTMLProps<HTMLDivElement>> = ({
    isPartnerSection,
    className,
}) => {
    const {
        actions,
        parameters: { supplementInfo },
    } = useContext(PensionContext);

    const getAccounts = () => {
        return (isPartnerSection ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? [];
    };

    const updateAccount = (index: number) => {
        let accounts = (isPartnerSection ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? [];

        return (newAccount: Account) => {
            accounts.splice(index, 1, newAccount);
            actions.setParameters(
                isPartnerSection
                    ? { supplementInfo: { partner: { accounts: accounts } } }
                    : { supplementInfo: { self: { accounts: accounts } } },
            );
        };
    };

    return (
        <div className={`${className} overflow-y-auto pr-2`} style={{ maxHeight: '52rem' }}>
            {getAccounts().map((acc, i) => (
                <div key={`account-${i}`} className="mb-4">
                    <AccountForm
                        formId={i}
                        updateAccount={updateAccount(i)}
                        currentAccount={acc}
                        isPartnerSection={isPartnerSection}
                    />
                </div>
            ))}
            {((isPartnerSection ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? []).length <
                MAX_NUMBER_SUPPLEMENT_ACCS && <AddAccountButton isPartnerSection={isPartnerSection} />}
        </div>
    );
};

const AccountForm: React.FC<
    {
        formId: number;
        isPartnerSection: boolean;
        currentAccount: Account;
        updateAccount: AccountReturnVoid;
    } & HTMLProps<HTMLDivElement>
> = ({ formId, isPartnerSection, className, currentAccount, updateAccount }) => {
    return (
        <div className={className}>
            <span className="font-medium text-xl mb-8">{`${formId + 1}e rekening`}</span>
            <AccountFormTypeSelect className="mb-8" updateAccount={updateAccount} currentAccount={currentAccount} />
            <AccountFormAmountInput
                className="mb-8"
                isPartnerSection={isPartnerSection}
                updateAccount={updateAccount}
                currentAccount={currentAccount}
            />
            <AccountFormDatePicker
                className="mb-8"
                updateAccount={updateAccount}
                currentAccount={currentAccount}
                isPartnerSection={isPartnerSection}
            />
            <RemoveAccountButton className="mb-8" isPartnerSection={isPartnerSection} formId={formId} />
        </div>
    );
};

const AddAccountButton: React.FC<{ isPartnerSection: boolean } & HTMLProps<HTMLDivElement>> = ({
    isPartnerSection,
    className,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);

    const getEmptySavingsAccount = () => {
        return {
            type: 'saving',
            recurringPaymentInterval: 'month',
        } as Account;
    };

    return (
        <Button
            buttonType="secondary"
            onClick={() => {
                actions.setParameters(
                    isPartnerSection
                        ? {
                              supplementInfo: {
                                  partner: {
                                      accounts: (supplementInfo?.partner?.accounts ?? []).concat(
                                          getEmptySavingsAccount(),
                                      ),
                                  },
                              },
                          }
                        : {
                              supplementInfo: {
                                  self: {
                                      accounts: (supplementInfo?.self?.accounts ?? []).concat(getEmptySavingsAccount()),
                                  },
                              },
                          },
                );
            }}
            className={`${className} mb-8`}
        >
            <span>
                {!isPartnerSection &&
                    ((supplementInfo?.self?.accounts ?? []).length > 0
                        ? t('pension-cocktail-fill-screen-supplement-add-btn')
                        : t('pension-cocktail-fill-screen-supplement-add-btn-first'))}
                {isPartnerSection &&
                    ((supplementInfo?.partner?.accounts ?? []).length > 0
                        ? t('pension-cocktail-fill-screen-supplement-add-btn')
                        : t('pension-cocktail-fill-screen-supplement-add-btn-first'))}
            </span>
        </Button>
    );
};

const AccountFormTypeSelect: React.FC<
    { updateAccount: AccountReturnVoid; currentAccount: Account } & HTMLProps<HTMLDivElement>
> = ({ currentAccount, className, updateAccount }) => {
    const { t } = useTranslation('pension');

    return (
        <div className={`flex flex-col flex-1 ${className}`}>
            <div className="my-2">{t('pension-cocktail-fill-screen-supplement-type-label')}</div>
            <span className="relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-white">
                <select
                    className="w-full px-0 py-1.5 pr-10 focus:outline-hidden appearance-none bg-white/0 cursor-pointer font-medium"
                    value={currentAccount.type}
                    onChange={(e) => {
                        updateAccount({
                            ...currentAccount,
                            type: e.target.value as AccountType,
                        });
                    }}
                >
                    <option value="investment">{t('pension-cocktail-fill-screen-supplement-type-investment')}</option>
                    <option value="saving">{t('pension-cocktail-fill-screen-supplement-type-savings')}</option>
                </select>
                <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
            </span>
        </div>
    );
};

const AccountFormAmountInput: React.FC<
    {
        isPartnerSection: boolean;
        currentAccount: Account;
        updateAccount: AccountReturnVoid;
    } & HTMLProps<HTMLDivElement>
> = ({ isPartnerSection, currentAccount, className, updateAccount }) => {
    const { t } = useTranslation('pension');
    const { t: tg } = useTranslation('general');
    const [supplementModalShown, setSupplementModalShown] = useState(false);

    const displayValue = !!currentAccount.predictedAmountAtEndAccount
        ? Math.ceil(currentAccount.predictedAmountAtEndAccount / 100)
        : '';

    const supplementAmountLabel = isPartnerSection
        ? t('pension-cocktail-fill-screen-supplement-amount-label-partner')
        : t('pension-cocktail-fill-screen-supplement-amount-label');

    return (
        <div className={className}>
            <div className="flex items-center justify-between" style={{ marginBottom: '-24px' }}>
                {supplementAmountLabel}
                <Tooltip
                    tooltipText={t('pension-cocktail-fill-screen-supplement-amount-tooltip-text')}
                    className="ml-2"
                >
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            </div>
            <div
                onClick={() => {
                    setSupplementModalShown(true);
                }}
                className="w-60 relative top-8 float-right flex flex-row justify-end text-bnd-green items-center cursor-pointer pr-4 hover:underline"
            >
                <span>{t('pension-cocktail-fill-screen-supplement-amount-btn')}</span>
                <IconElements.Calculator className="ml-2 h-6 w-6 text-bnd-green" />
            </div>
            <div>
                <CurrencyInput
                    type="text"
                    placeholder={tg('euro-placeholder')}
                    className={
                        'bg-white text-black w-full py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium'
                    }
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={displayValue}
                    onValueChange={(value = '', _) => {
                        const newPredictedAmount = reverseFormatNumber(value, 'nl-NL') * 100;
                        const newPredictedAmountDisplayValue = Math.ceil(newPredictedAmount / 100);
                        if (newPredictedAmountDisplayValue !== displayValue) {
                            updateAccount({
                                recurringPaymentInterval: currentAccount.recurringPaymentInterval,
                                type: currentAccount.type,
                                availableAt: currentAccount.availableAt,
                                predictedAmountAtEndAccount: newPredictedAmount,
                                changedManually: true,
                            });
                        }
                    }}
                />
            </div>
            {supplementModalShown && (
                <SupplementGeneralModal
                    isShown={supplementModalShown}
                    isPartnerSection={isPartnerSection}
                    onClose={() => {
                        setSupplementModalShown(false);
                    }}
                    account={currentAccount}
                    updateAccount={updateAccount}
                />
            )}
        </div>
    );
};

const AccountFormDatePicker: React.FC<
    {
        updateAccount: AccountReturnVoid;
        currentAccount: Account;
        isPartnerSection: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ currentAccount, className, updateAccount, isPartnerSection }) => {
    const { t } = useTranslation('pension');
    const { t: tg } = useTranslation('general');

    const onAutoFillWithPensionDate = (pensionAgeDate: string) => {
        updateAccount({
            ...currentAccount,
            availableAt: pensionAgeDate,
        });
    };

    return (
        <div className={className}>
            <div className="flex items-center justify-between">
                {t('pension-cocktail-fill-screen-supplement-date-label')}
                <Tooltip tooltipText={t('pension-cocktail-fill-screen-supplement-date-tooltip-text')} className="ml-2">
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            </div>
            <div className="relative mb-16">
                <input
                    className={`${
                        !!currentAccount.availableAt ? 'text-black' : 'text-gray-400'
                    } absolute w-full py-2 px-4 border rounded-lg focus:outline-hidden bg-white border-gray-200 font-medium`}
                    placeholder={tg('date-placeholder')}
                    type="Date"
                    value={currentAccount.availableAt ?? ''}
                    onChange={(e) => {
                        updateAccount({
                            ...currentAccount,
                            availableAt: e.target.value,
                        });
                    }}
                ></input>
                <IconElements.Calendar className="absolute top-2 right-4" />
            </div>
            <AutoFillMoneyAvailableAtDateWithPensionDate
                onClick={onAutoFillWithPensionDate}
                isPartnerSection={isPartnerSection}
            />
        </div>
    );
};

const RemoveAccountButton: React.FC<{ formId: number; isPartnerSection: boolean } & HTMLProps<HTMLDivElement>> = ({
    formId,
    isPartnerSection,
    className,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);
    const [formShown, setFormShown] = useState(false);

    const removeAccount = (index: number) => {
        let accounts = (isPartnerSection ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? [];
        accounts.splice(index, 1);
        actions.setParameters(
            isPartnerSection
                ? { supplementInfo: { partner: { accounts: accounts } } }
                : { supplementInfo: { self: { accounts: accounts } } },
        );
    };

    return (
        <>
            <Button
                buttonType="secondary"
                className={`${className} `}
                onClick={() => {
                    setFormShown(true);
                }}
            >
                {t('pension-cocktail-fill-screen-supplement-remove-btn')}
            </Button>
            <RemoveAccountModal
                formId={formId}
                onRemoveAccount={removeAccount}
                isShown={formShown}
                onClose={() => {
                    setFormShown(false);
                }}
            />
        </>
    );
};

function AreAccountsValid(accounts: Account[]): boolean {
    return accounts.every(isAccountValid);
}

function isAccountValid(account: Account): boolean {
    return !!account.availableAt && !!account.predictedAmountAtEndAccount;
}
